import { PillsVariant } from "../../common-types/Pill";
import PillsGroup from "../PillsGroup/PillsGroup";
import eventStyles from "../EventSummary/EventSummary.module.css";
import publicStyles from "./PublicEventContent.module.css";
import { PublicEvent } from "@unruly-global/api-types";
import EventIcon from "@mui/icons-material/Event";
import NewspaperOutlinedIcon from "@mui/icons-material/NewspaperOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import Markdown from "react-markdown";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import { formatDate, getDomainFromUrl } from "../../helper/helper";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import LocationIcon from "@mui/icons-material/LocationOn";
import ErrorIcon from "@mui/icons-material/Error";
import ErrorPage from "../../views/Error/ErrorPage";
import EventContentSkeleton from "../EventContent/EventContentSkeleton";

interface PublicEventContentProps {
  event?: PublicEvent;
  isLoading: boolean;
  isError: boolean;
  error?: Error;
  accentColor?: string;
}

const PublicEventContent: React.FC<PublicEventContentProps> = ({
  event,
  isLoading,
  isError,
  error,
  accentColor,
}) => {
  if (isLoading) {
    return <EventContentSkeleton />;
  }
  if (isError) {
    console.log(error);
    return <ErrorPage />;
  }
  const retrievalTime = event?.retrievalTime
    ? new Date(event?.retrievalTime).toISOString()
    : null;
  const isInternalEvent = event?.isInternalEvent ?? false;
  const sourceLabel = isInternalEvent
    ? "Unruly / AFP"
    : event?.newsSourceUrl
      ? (getDomainFromUrl(event.newsSourceUrl) ?? "No Source")
      : "No Source";
  const sourceHref = isInternalEvent
    ? undefined
    : event?.newsSourceUrl || undefined;
  return (
    <>
      <style>
        {`
          .accented-text {
            color: ${accentColor ?? "inherit"};
          }
          .accented-background {
            background-color: ${accentColor ? accentColor + "40" : "var(--grey)"};
            border: 1px solid ${accentColor ? accentColor + "40" : "var(--grey)"};
          }
        `}
      </style>
      <div className={publicStyles["container"]}>
        <div className={eventStyles["title"]}>
          <h2>{event?.name}</h2>
          <PillsGroup
            pills={[
              {
                variant: PillsVariant.default,
                value: event?.startDate ?? "",
                label: "Date",
                icon: EventIcon,
              },
              {
                variant: PillsVariant.default,
                value: event?.location?.name ?? "No Country",
                label: "Location",
                icon: LocationIcon,
              },
              {
                variant: PillsVariant.default,
                value: event?.impactLevel ?? "",
                label: "Impact Level",
                icon: ErrorIcon,
                tooltipText:
                  "Impact levels gauge an event's influence on global politics, from localized effects (Level 1) to transformative shifts in the international political order (Level 4)",
              },
              {
                variant: PillsVariant.default,
                value: sourceLabel,
                href: sourceHref,
                label: "Source",
                icon: NewspaperOutlinedIcon,
              },
            ]}
          />
        </div>
      </div>
      <div className={publicStyles["container"]}>
        <div className={eventStyles["broader-context"]}>
          <div className={eventStyles["secondary-header"]}>
            <h4 className="accented-text">Event Summary</h4>
            <AutoAwesomeOutlinedIcon className={eventStyles["icon"]} />
          </div>
          <Markdown>{event?.broaderContext.content}</Markdown>
          <div className={eventStyles["secondary-header"]}>
            <h4 className="accented-text">Referenced Events</h4>
          </div>
          <List dense={true} className={publicStyles["broader-context-list"]}>
            {event?.broaderContext.events.map((refEvent) => (
              <ListItem
                key={refEvent.id}
                className={
                  publicStyles["broader-context-event"] + " accented-background"
                }
              >
                <ListItemText
                  className={publicStyles["broader-context-event-text"]}
                  primary={refEvent.name}
                  secondaryTypographyProps={{ component: "div" }}
                  secondary={
                    <PillsGroup
                      pills={[
                        {
                          variant: PillsVariant.default,
                          value: refEvent.date,
                          label: "Date",
                          icon: EventIcon,
                        },
                        {
                          variant: PillsVariant.default,
                          value: refEvent.isInternalEvent
                            ? "Unruly / AFP"
                            : refEvent.sourceUrl
                              ? (getDomainFromUrl(refEvent.sourceUrl) ??
                                "No Source")
                              : "No Source",
                          href: refEvent.isInternalEvent
                            ? undefined
                            : refEvent.sourceUrl || undefined,
                          label: "Source",
                          icon: NewspaperOutlinedIcon,
                        },
                        ...(event?.id.startsWith(refEvent.id.split("...")[0]) &&
                        event?.id.endsWith(refEvent.id.split("...")[1])
                          ? [
                              {
                                variant: PillsVariant.default,
                                tooltipText:
                                  "You are currently viewing this event",
                                value: "This event",
                                icon: VisibilityIcon,
                              },
                            ]
                          : []),
                      ]}
                    />
                  }
                />
              </ListItem>
            ))}
          </List>
        </div>
        {retrievalTime && (
          <div className={publicStyles["retrieval-time"]}>
            Last updated at {new Date(retrievalTime).toLocaleTimeString()} on{" "}
            {formatDate(retrievalTime)}
          </div>
        )}
      </div>
    </>
  );
};

export default PublicEventContent;
